import { useEffect } from 'react';
import { PERMISSIONS } from '@helios/authz';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  developmentChecklistClient,
  type ChecklistImportInput,
  type ChecklistPatchInput,
} from '@/services/developmentChecklistClient';
import { sessionScopeKey, useSessionScopeFence } from './useSessionScope';

export function useDevelopmentChecklist() {
  const { identity, enabled, isCurrent } = useSessionScopeFence(PERMISSIONS.PLATFORM_ADMIN);
  const scope = JSON.stringify(sessionScopeKey(identity));
  const queryClient = useQueryClient();
  const queryKey = ['development-checklist', ...sessionScopeKey(identity)];
  const query = useQuery({
    queryKey,
    enabled,
    placeholderData: () => undefined,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!isCurrent(identity)) throw new Error('Checklist access required.');
      const result = await developmentChecklistClient.read({
        signal,
        sessionOrigin: identity.sessionOrigin,
      });
      signal.throwIfAborted();
      if (!isCurrent(identity)) throw new Error('Your checklist access changed.');
      return result;
    },
  });
  const preview = useMutation({
    gcTime: 0,
    mutationFn: async (input: Omit<ChecklistImportInput, 'expectedVersion'>) => {
      const origin = identity;
      if (!isCurrent(origin)) throw new Error('Checklist access required.');
      const result = await developmentChecklistClient.preview(input, {
        sessionOrigin: origin.sessionOrigin,
      });
      if (!isCurrent(origin)) throw new Error('Your checklist access changed.');
      return result;
    },
  });
  const importItems = useMutation({
    gcTime: 0,
    mutationFn: async (input: ChecklistImportInput) => {
      const origin = identity;
      if (!isCurrent(origin)) throw new Error('Checklist access required.');
      const result = await developmentChecklistClient.import(input, {
        sessionOrigin: origin.sessionOrigin,
      });
      if (!isCurrent(origin)) throw new Error('Your checklist access changed.');
      queryClient.setQueryData(queryKey, result);
      return result;
    },
  });
  const update = useMutation({
    gcTime: 0,
    mutationFn: async (input: { itemId: string; patch: ChecklistPatchInput }) => {
      const origin = identity;
      if (!isCurrent(origin)) throw new Error('Checklist access required.');
      const result = await developmentChecklistClient.update(input.itemId, input.patch, {
        sessionOrigin: origin.sessionOrigin,
      });
      if (!isCurrent(origin)) throw new Error('Your checklist access changed.');
      queryClient.setQueryData(queryKey, result);
      return result;
    },
  });
  const resetPreview = preview.reset;
  const resetImport = importItems.reset;
  const resetUpdate = update.reset;
  useEffect(() => {
    resetPreview();
    resetImport();
    resetUpdate();
  }, [scope, resetPreview, resetImport, resetUpdate]);
  return {
    scope,
    identity,
    isCurrent,
    enabled,
    query: { ...query, data: enabled && !query.isError ? query.data : undefined },
    preview,
    importItems,
    update,
  };
}
